// apps/client/src/components/BallDisplay.tsx
//
// Animated ball display - Uses new UI Kit GameBall
// Business logic preserved, visual layer delegated to UI Kit
//

import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAudio } from '../providers/AudioProvider';

interface BallDisplayProps {
    lastNumber: number | null;
    drawnNumbers: number[];
}

export function BallDisplay({ lastNumber, drawnNumbers }: BallDisplayProps) {
    const { playPop } = useAudio();

    // Get the last 10 balls for the history display
    const recentBalls = drawnNumbers.slice(-10).reverse();

    useEffect(() => {
        if (lastNumber !== null) {
            playPop();
        }
    }, [lastNumber, playPop]);

    return (
        <div className="flex flex-col items-center justify-center w-full z-10 max-w-sm mx-auto">
            {/* 2.5D Ball Display Area */}
            <div className="relative px-4 py-8 flex flex-col items-center justify-center min-h-[160px] w-full">
                <AnimatePresence mode="popLayout">
                    {lastNumber !== null ? (
                        <motion.div
                            key={lastNumber}
                            initial={{ y: -100, scale: 0.5, rotateX: 90, opacity: 0 }}
                            animate={{ y: 0, scale: 1, rotateX: 0, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0, filter: 'blur(10px)' }}
                            transition={{ type: 'spring', damping: 12, stiffness: 100 }}
                            className="relative w-32 h-32 rounded-full flex items-center justify-center shadow-[0_20px_40px_rgba(0,0,0,0.5),inset_0_-10px_20px_rgba(0,0,0,0.5),inset_0_10px_20px_rgba(255,255,255,0.8)] bg-gradient-to-br from-[#38BDF8] to-[#2563EB] transform-style-3d border-[3px] border-blue-300"
                        >
                            <div className="absolute top-2 left-4 w-8 h-4 bg-white/70 rounded-full blur-[2px] transform -rotate-12" />
                            <span className="text-5xl font-black text-[#0F172A] drop-shadow-md font-display transform translate-z-10">{lastNumber}</span>
                        </motion.div>
                    ) : (
                        <div className="w-32 h-32 flex items-center justify-center text-white/40 font-bold text-center">
                            Waiting for<br />first ball...
                        </div>
                    )}
                </AnimatePresence>

                {/* Progress bar */}
                <div className="w-full max-w-xs mt-6">
                    <div className="flex justify-between text-[11px] font-bold text-white/50 mb-1.5 uppercase tracking-wide">
                        <span>Drawn: {drawnNumbers.length}</span>
                        <span>Total: 90</span>
                    </div>
                    <div className="h-1.5 bg-black/40 rounded-full overflow-hidden shadow-inner border border-white/5">
                        <motion.div
                            className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full shadow-[0_0_10px_rgba(56,189,248,0.5)]"
                            animate={{ width: `${(drawnNumbers.length / 90) * 100}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* History Ribbon */}
            <div className="px-4 py-3 bg-white/5 border border-white/10 backdrop-blur-md rounded-3xl shadow-inner flex gap-2 overflow-x-auto hide-scrollbar whitespace-nowrap mask-edges-x w-full">
                {recentBalls.map((n, i) => (
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        key={`${n}-${i}`}
                        className={`inline-flex shrink-0 w-10 h-10 rounded-full items-center justify-center text-sm font-black shadow-lg ${i === 0
                            ? 'bg-gradient-to-br from-cyan-400 to-blue-500 text-white border border-blue-300'
                            : 'bg-gradient-to-br from-white/20 to-white/10 text-white border border-white/20'
                            }`}
                    >
                        {n}
                    </motion.div>
                ))}
            </div>
        </div>
    );
}
